import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  const [isVisible, setIsVisible] = useState({});
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const testimonials = document.querySelectorAll('.testimonial-item');
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, []);

  const stats = [
    { number: '150+', label: 'Properties Sold', icon: '🏠' },
    { number: '15+', label: 'Years Experience', icon: '⭐' },
    { number: '98%', label: 'Client Satisfaction', icon: '😊' },
  ];

  

  

  const values = [
    {
      icon: '🎯',
      title: 'Excellence',
      description: 'We strive for excellence in every transaction, ensuring our clients receive the highest level of service.'
    },
    {
      icon: '🤝',
      title: 'Integrity',
      description: 'Trust and transparency are the foundations of our business. We build lasting relationships through honest dealings.'
    },
    {
      icon: '💡',
      title: 'Innovation',
      description: 'We embrace cutting-edge technology and creative solutions to provide superior real estate experiences.'
    },
    {
      icon: '🌟',
      title: 'Client-Focused',
      description: 'Your success is our success. We tailor our approach to meet your unique needs and exceed expectations.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20 overflow-x-hidden">
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-32 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-yellow-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
            About <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">THINKREALTY</span>
          </h1>
          <p className="text-2xl opacity-90 max-w-4xl mx-auto leading-relaxed mb-12">
            Transforming real estate experiences through innovation, expertise, and unwavering commitment to our clients' success
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to="/properties"
              className="group relative inline-flex items-center justify-center px-8 sm:px-12 py-5 bg-white text-blue-900 hover:bg-gray-100 font-bold text-lg rounded-2xl transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl overflow-hidden"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span>Explore Properties</span>
              </span>
            </Link>
            <Link 
              to="/agents"
              className="group relative inline-flex items-center justify-center px-8 sm:px-12 py-5 bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold text-lg rounded-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 04 0z" />
                </svg>
                <span>Meet Our Team</span>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Animated Stats Section */}
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                id={`stat-${index}`}
                data-animate
                className={`text-center transform transition-all duration-1000 ${
                  isVisible[`stat-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className="group bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100">
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {stat.icon}
                  </div>
                  <div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium uppercase text-sm tracking-wider">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div 
              id="story-content"
              data-animate
              className={`transform transition-all duration-1000 ${
                isVisible['story-content'] ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
              } text-center lg:text-left`}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                <span className="text-black">Our </span><span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Story</span>
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed break-words">
                <p>
                  Founded in 2008 with a vision to revolutionize the real estate industry, THINKREALTY began as a small boutique firm with big dreams. Our founders, driven by a passion for exceptional service and innovative solutions, set out to create a company that would redefine what clients could expect from their real estate experience.
                </p>
                <p>
                  Over the years, we've grown from a team of three to a powerhouse of over 50 dedicated professionals, yet we've never lost sight of our core values: integrity, innovation, and an unwavering commitment to our clients' success.
                </p>
                <p>
                  Today, THINKREALTY stands as a leader in the luxury real estate market, having facilitated over ₹2Cr in transactions while maintaining the personalized touch that our clients have come to love and trust.
                </p>
              </div>
              
              {/* Mission & Vision */}
              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 bg-blue-50 rounded-2xl border-l-4 border-blue-500 text-center md:text-left">
                  <h3 className="text-xl font-bold text-blue-900 mb-3 flex items-center justify-center md:justify-start">
                    <span className="mr-3">🎯</span>Our Mission
                  </h3>
                  <p className="text-blue-800">
                    To provide exceptional real estate services that exceed expectations and create lasting relationships built on trust, expertise, and results.
                  </p>
                </div>
                <div className="p-6 bg-purple-50 rounded-2xl border-l-4 border-purple-500 text-center md:text-left">
                  <h3 className="text-xl font-bold text-purple-900 mb-3 flex items-center justify-center md:justify-start">
                    <span className="mr-3">🔮</span>Our Vision
                  </h3>
                  <p className="text-purple-800">
                    To be the most trusted and innovative real estate company, transforming how people buy, sell, and invest in properties.
                  </p>
                </div>
              </div>
            </div>
            
            <div 
              id="story-image"
              data-animate
              className={`relative transform transition-all duration-1000 delay-300 overflow-hidden ${
                isVisible['story-image'] ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
              }`}
            >
              <div className="relative">
                <img 
                  src="images/about.jpg"
                  alt="Modern office building"
                  className="w-full h-56 sm:h-72 md:h-96 object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-3xl"></div>
                
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div 
            id="values-header"
            data-animate
            className={`text-center mb-20 transform transition-all duration-1000 ${
              isVisible['values-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-black">Our Core</span> <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              The principles that guide everything we do and shape the exceptional experiences we create for our clients
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                id={`value-${index}`}
                data-animate
                className={`group bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 ${
                  isVisible[`value-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                } text-center md:text-left`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

     

      

      {/* Enhanced CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-8">
            Ready to Start Your <br />
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              Real Estate Journey?
            </span>
          </h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto opacity-90 leading-relaxed">
            Join thousands of satisfied clients who have trusted THINKREALTY with their most important real estate decisions. Let's make your property dreams a reality.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to="/contact"
              className="group relative inline-flex items-center justify-center px-8 sm:px-12 py-5 bg-white text-blue-900 hover:bg-gray-100 font-bold text-lg rounded-2xl transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl overflow-hidden"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>Start Your Journey</span>
              </span>
            </Link>
            <Link 
              to="/properties"
              className="group relative inline-flex items-center justify-center px-8 sm:px-12 py-5 bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold text-lg rounded-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Explore Properties</span>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
